import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Divider,
  Paper,
  Tooltip,
  alpha,
  useTheme,
  InputAdornment,
} from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";

const STATUS_COLORS = {
  open: "error",
  answered: "info",
  closed: "default",
};

const PRIORITY_COLORS = {
  low: "success",
  medium: "warning",
  high: "error",
};

const SupportTickets = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();

  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const limit = 15;

  // View dialog
  const [viewDialog, setViewDialog] = useState({
    open: false,
    ticket: null,
    messages: [],
  });
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);

  useEffect(() => {
    loadTickets();
  }, [page, search, statusFilter]);

  const loadTickets = async () => {
    const res = await hitAxios({
      path: `/api/tickets/admin-get-all?page=${page}&limit=${limit}&search=${search}&status=${statusFilter}`,
      admin: true,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      setTickets(res.data.data);
      setTotal(res.data.total);
    }
  };

  const openTicket = async (ticket) => {
    const res = await hitAxios({
      path: `/api/tickets/admin-get/${ticket.uid}`,
      admin: true,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      setViewDialog({
        open: true,
        ticket: res.data.ticket,
        messages: res.data.messages,
      });
      setReplyText("");
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setReplying(true);
    const res = await hitAxios({
      path: "/api/tickets/admin-reply",
      post: true,
      admin: true,
      obj: { ticket_uid: viewDialog.ticket.uid, message: replyText },
    });
    setReplying(false);
    if (res?.data?.success) {
      setReplyText("");
      // Refresh messages
      const updated = await hitAxios({
        path: `/api/tickets/admin-get/${viewDialog.ticket.uid}`,
        admin: true,
        showSnackbar: false,
      });
      if (updated?.data?.success) {
        setViewDialog((prev) => ({
          ...prev,
          ticket: updated.data.ticket,
          messages: updated.data.messages,
        }));
      }
      loadTickets();
    }
  };

  const updateStatus = async (ticket_uid, status) => {
    await hitAxios({
      path: "/api/tickets/admin-update-status",
      post: true,
      admin: true,
      obj: { ticket_uid, status },
    });
    loadTickets();
    if (viewDialog.open && viewDialog.ticket?.uid === ticket_uid) {
      setViewDialog((prev) => ({
        ...prev,
        ticket: { ...prev.ticket, status },
      }));
    }
  };

  const deleteTicket = async (ticket_uid) => {
    if (!window.confirm("Delete this ticket and all messages?")) return;
    await hitAxios({
      path: "/api/tickets/admin-delete",
      post: true,
      admin: true,
      obj: { ticket_uid },
    });
    loadTickets();
    if (viewDialog.ticket?.uid === ticket_uid)
      setViewDialog({ open: false, ticket: null, messages: [] });
  };

  const formatDate = (d) => new Date(d).toLocaleString();

  return (
    <Box>
      <PageHeader
        title="Support Tickets"
        subtitle={`${total} total tickets`}
        icon={SupportAgentIcon}
      />

      {/* ── Filters ── */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3}>
        <TextField
          size="small"
          placeholder="Search by user, email or subject..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, maxWidth: 360 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="answered">Answered</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <IconButton
            onClick={loadTickets}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ── Table ── */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    "Ticket",
                    "User",
                    "Subject",
                    "Priority",
                    "Status",
                    "Last Updated",
                    "Actions",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{ py: 6, color: "text.secondary" }}
                    >
                      No tickets found
                    </TableCell>
                  </TableRow>
                ) : (
                  tickets.map((ticket) => (
                    <TableRow
                      key={ticket.uid}
                      sx={{
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                        },
                        cursor: "pointer",
                      }}
                      onClick={() => openTicket(ticket)}
                    >
                      <TableCell>
                        <Typography
                          variant="caption"
                          fontFamily="monospace"
                          color="text.secondary"
                        >
                          #{ticket.uid?.substring(0, 8)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              bgcolor: theme.palette.primary.main,
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            {ticket.user_name?.[0]?.toUpperCase() || "U"}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ lineHeight: 1.2 }}
                            >
                              {ticket.user_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {ticket.user_email}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          noWrap
                          sx={{ maxWidth: 220 }}
                        >
                          {ticket.subject}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.priority}
                          size="small"
                          color={PRIORITY_COLORS[ticket.priority] || "default"}
                          sx={{
                            fontWeight: 700,
                            height: 20,
                            fontSize: "0.65rem",
                            textTransform: "capitalize",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.status}
                          size="small"
                          color={STATUS_COLORS[ticket.status] || "default"}
                          sx={{
                            fontWeight: 700,
                            height: 20,
                            fontSize: "0.65rem",
                            textTransform: "capitalize",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(ticket.updatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="View Ticket">
                            <IconButton
                              size="small"
                              onClick={() => openTicket(ticket)}
                              sx={{
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.1,
                                  ),
                                },
                              }}
                            >
                              <VisibilityIcon
                                sx={{ fontSize: 16, color: "primary.main" }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Ticket">
                            <IconButton
                              size="small"
                              onClick={() => deleteTicket(ticket.uid)}
                              sx={{
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                },
                              }}
                            >
                              <DeleteIcon
                                sx={{ fontSize: 16, color: "error.main" }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "center",
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Pagination
              count={Math.ceil(total / limit)}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              shape="rounded"
            />
          </Box>
        </CardContent>
      </Card>

      {/* ── View / Reply Dialog ── */}
      <Dialog
        open={viewDialog.open}
        onClose={() =>
          setViewDialog({ open: false, ticket: null, messages: [] })
        }
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            height: "85vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{ pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
                <Typography fontWeight={700} variant="h6">
                  {viewDialog.ticket?.subject}
                </Typography>
                <Chip
                  label={viewDialog.ticket?.status}
                  size="small"
                  color={STATUS_COLORS[viewDialog.ticket?.status] || "default"}
                  sx={{ fontWeight: 700, textTransform: "capitalize" }}
                />
                <Chip
                  label={viewDialog.ticket?.priority}
                  size="small"
                  color={
                    PRIORITY_COLORS[viewDialog.ticket?.priority] || "default"
                  }
                  sx={{ fontWeight: 700, textTransform: "capitalize" }}
                />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                  sx={{
                    width: 20,
                    height: 20,
                    bgcolor: theme.palette.primary.main,
                    fontSize: 10,
                  }}
                >
                  {viewDialog.ticket?.user_name?.[0]?.toUpperCase()}
                </Avatar>
                <Typography variant="caption" color="text.secondary">
                  {viewDialog.ticket?.user_name} ·{" "}
                  {viewDialog.ticket?.user_email}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  · #{viewDialog.ticket?.uid?.substring(0, 8)}
                </Typography>
              </Stack>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value={viewDialog.ticket?.status || "open"}
                  onChange={(e) =>
                    updateStatus(viewDialog.ticket?.uid, e.target.value)
                  }
                  sx={{ fontSize: "0.75rem", borderRadius: 2 }}
                >
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="answered">Answered</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={() =>
                  setViewDialog({ open: false, ticket: null, messages: [] })
                }
                sx={{
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.15) },
                }}
              >
                <CloseIcon fontSize="small" sx={{ color: "error.main" }} />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>

        {/* Messages */}
        <DialogContent sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
          <Stack spacing={2}>
            {viewDialog.messages.map((msg, i) => {
              const isAdmin = msg.sender_role === "admin";
              return (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    justifyContent: isAdmin ? "flex-end" : "flex-start",
                  }}
                >
                  <Box sx={{ maxWidth: "75%" }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      mb={0.5}
                      justifyContent={isAdmin ? "flex-end" : "flex-start"}
                    >
                      {!isAdmin && (
                        <Avatar
                          sx={{
                            width: 22,
                            height: 22,
                            bgcolor: theme.palette.primary.main,
                            fontSize: 10,
                          }}
                        >
                          {viewDialog.ticket?.user_name?.[0]?.toUpperCase()}
                        </Avatar>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        {isAdmin ? "Admin" : viewDialog.ticket?.user_name}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {formatDate(msg.createdAt)}
                      </Typography>
                      {isAdmin && (
                        <Avatar
                          sx={{
                            width: 22,
                            height: 22,
                            bgcolor: theme.palette.warning.main,
                            fontSize: 10,
                          }}
                        >
                          A
                        </Avatar>
                      )}
                    </Stack>
                    <Paper
                      sx={{
                        p: 1.75,
                        borderRadius: isAdmin
                          ? "12px 4px 12px 12px"
                          : "4px 12px 12px 12px",
                        bgcolor: isAdmin
                          ? alpha(theme.palette.primary.main, 0.1)
                          : theme.palette.mode === "dark"
                            ? alpha(theme.palette.background.paper, 0.8)
                            : "#F9FAFB",
                        border: `1px solid ${
                          isAdmin
                            ? alpha(theme.palette.primary.main, 0.2)
                            : theme.palette.divider
                        }`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}
                      >
                        {msg.message}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>

        {/* Reply Box */}
        {viewDialog.ticket?.status !== "closed" && (
          <Box sx={{ p: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-end">
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Type your reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.ctrlKey) sendReply();
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <Button
                variant="contained"
                endIcon={<SendIcon />}
                onClick={sendReply}
                disabled={replying || !replyText.trim()}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  py: 1.25,
                }}
              >
                {replying ? "Sending..." : "Reply"}
              </Button>
            </Stack>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ mt: 0.5, display: "block" }}
            >
              Ctrl+Enter to send
            </Typography>
          </Box>
        )}
        {viewDialog.ticket?.status === "closed" && (
          <Box
            sx={{
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              textAlign: "center",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              This ticket is closed. Change status to reply.
            </Typography>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default SupportTickets;
